import { useEffect, useRef, useState } from "react";
import { Avatar, Box, Typography } from "@mui/joy";
import { Building2, ImagePlus, Pencil, Trash2 } from "lucide-react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { Button, IconButton } from "../../../shared";

const ACCEPTED_TYPES = "image/png,image/jpeg,image/jpg,image/webp,image/gif";

type LogoUploadFieldProps = {
    /** When false, show logo preview with an Edit action only. Defaults to always-editable. */
    editing?: boolean;
    onEdit?: () => void;
    onCancelEdit?: () => void;
};

/** Local logo picker with preview. Upload API can be wired later. */
export function LogoUploadField({
    editing = true,
    onEdit,
    onCancelEdit,
}: LogoUploadFieldProps = {}) {
    const { control, setValue } = useFormContext();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const logoFile = useWatch({ control, name: "LogoFile" }) as File | null | undefined;
    const logoUrl = useWatch({ control, name: "LogoUrl" }) as string | undefined;
    const orgName = useWatch({ control, name: "Name" }) as string | undefined;
    const [localPreview, setLocalPreview] = useState("");

    useEffect(() => {
        if (!logoFile) {
            setLocalPreview("");
            return;
        }
        const objectUrl = URL.createObjectURL(logoFile);
        setLocalPreview(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [logoFile]);

    const previewUrl = localPreview || logoUrl || "";

    const clearLogo = () => {
        setValue("LogoFile", null, { shouldDirty: true });
        setValue("LogoUrl", "", { shouldDirty: true });
        if (inputRef.current) {
            inputRef.current.value = "";
        }
    };

    if (!editing) {
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                }}
            >
                <Avatar
                    src={previewUrl || undefined}
                    alt={orgName || "Organization logo"}
                    sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "4px",
                        bgcolor: "primary.softBg",
                        color: "primary.solidBg",
                    }}
                >
                    <Building2 size={26} />
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography level="title-sm">Organization logo</Typography>
                    <Typography level="body-xs" sx={{ color: "text.secondary" }}>
                        {previewUrl
                            ? "Current logo on file for this organization."
                            : "No logo uploaded yet."}
                    </Typography>
                </Box>

                {onEdit ? (
                    <Button
                        type="button"
                        size="sm"
                        variant="soft"
                        color="primary"
                        startDecorator={<Pencil size={14} />}
                        onClick={onEdit}
                        sx={{ borderRadius: "4px", flexShrink: 0 }}
                    >
                        Edit
                    </Button>
                ) : null}
            </Box>
        );
    }

    return (
        <Controller
            name="LogoFile"
            control={control}
            render={({ field: { onChange } }) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                    }}
                >
                    <Avatar
                        src={previewUrl || undefined}
                        alt="Organization logo"
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: "4px",
                            bgcolor: "primary.softBg",
                            color: "primary.solidBg",
                        }}
                    >
                        <Building2 size={26} />
                    </Avatar>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography level="title-sm">Organization logo</Typography>
                        <Typography level="body-xs" sx={{ color: "text.secondary", mb: 1 }}>
                            Upload a square PNG or JPG. Image upload API will be connected later.
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                            <Button
                                type="button"
                                size="sm"
                                variant="soft"
                                color="primary"
                                startDecorator={<ImagePlus size={16} />}
                                onClick={() => inputRef.current?.click()}
                                sx={{ borderRadius: "4px" }}
                            >
                                {previewUrl ? "Replace image" : "Upload image"}
                            </Button>
                            {previewUrl ? (
                                <IconButton
                                    type="button"
                                    size="sm"
                                    variant="outlined"
                                    color="neutral"
                                    aria-label="Remove logo"
                                    onClick={clearLogo}
                                    sx={{ borderRadius: "4px" }}
                                >
                                    <Trash2 size={16} />
                                </IconButton>
                            ) : null}
                            {onCancelEdit ? (
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="plain"
                                    color="neutral"
                                    onClick={onCancelEdit}
                                    sx={{ borderRadius: "4px" }}
                                >
                                    Done
                                </Button>
                            ) : null}
                        </Box>
                        {logoFile ? (
                            <Typography
                                level="body-xs"
                                sx={{ color: "text.tertiary", mt: 0.75 }}
                            >
                                {logoFile.name}
                            </Typography>
                        ) : null}
                    </Box>

                    <input
                        ref={inputRef}
                        type="file"
                        accept={ACCEPTED_TYPES}
                        hidden
                        onChange={(event) => {
                            const file = event.target.files?.[0] || null;
                            onChange(file);
                            if (file) {
                                setValue("LogoUrl", "", { shouldDirty: true });
                            }
                        }}
                    />
                </Box>
            )}
        />
    );
}
